import { ref, get } from 'firebase/database';
import { db } from '../lib/firebase';
import { auth } from '../lib/firebase';

export const getAdminStatistics = async () => {
  try {
    // Vérifier d'abord si l'utilisateur est admin
    const adminRef = ref(db, `admins/${auth.currentUser?.uid}`);
    const adminSnapshot = await get(adminRef);
    
    if (!adminSnapshot.exists() || !adminSnapshot.val()) {
      throw new Error('Accès non autorisé');
    }

    // Récupérer la liste des administrateurs
    const adminsRef = ref(db, 'admins');
    const adminsSnapshot = await get(adminsRef);
    const adminIds = adminsSnapshot.exists() ? Object.keys(adminsSnapshot.val()) : [];

    // Récupérer les données des différentes collections
    const [usersSnapshot, estimatesSnapshot, creditsSnapshot, purchasesSnapshot] = await Promise.all([
      get(ref(db, 'profiles')),
      get(ref(db, 'estimates')),
      get(ref(db, 'credits')),
      get(ref(db, 'credit_purchases'))
    ]);

    // Initialiser les statistiques avec des valeurs par défaut
    const statistics = {
      totalUsers: 0,
      quickEstimates: 0,
      detailedEstimates: 0,
      totalCredits: 0,
      revenue: {
        total: 0,
        byYear: {} as Record<string, number>
      },
      activeUsers: {
        daily: 0,
        weekly: 0,
        monthly: 0
      }
    };

    // Calculer les statistiques en excluant les administrateurs
    if (usersSnapshot.exists()) {
      const users = Object.entries(usersSnapshot.val());
      statistics.totalUsers = users.filter(([userId]) => !adminIds.includes(userId)).length;
    }
    
    if (estimatesSnapshot.exists()) {
      Object.entries(estimatesSnapshot.val() || {}).forEach(([userId, userEstimates]: [string, any]) => {
        // Ignorer les estimations des administrateurs
        if (!adminIds.includes(userId)) {
          Object.values(userEstimates).forEach((estimate: any) => {
            if (estimate.type === 'quick') {
              statistics.quickEstimates++;
            } else if (estimate.type === 'detailed') {
              statistics.detailedEstimates++;
            }
          });
        }
      });
    }

    if (purchasesSnapshot.exists()) {
      Object.entries(purchasesSnapshot.val() || {}).forEach(([purchaseId, purchase]: [string, any]) => {
        // Ignorer les achats des administrateurs
        if (!adminIds.includes(purchase.userId)) {
          if (purchase.status === 'completed') {
            statistics.totalCredits += purchase.credits;
            statistics.revenue.total += purchase.amount;
            
            const year = new Date(purchase.createdAt).getFullYear().toString();
            statistics.revenue.byYear[year] = (statistics.revenue.byYear[year] || 0) + purchase.amount;
          }
        }
      });
    }

    // Calculer les utilisateurs actifs en excluant les administrateurs
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    if (estimatesSnapshot.exists()) {
      const estimates = estimatesSnapshot.val();
      const userLastActivity: Record<string, Date> = {};

      Object.entries(estimates).forEach(([userId, userEstimates]: [string, any]) => {
        // Ignorer l'activité des administrateurs
        if (!adminIds.includes(userId)) {
          Object.values(userEstimates).forEach((estimate: any) => {
            const activityDate = new Date(estimate.createdAt);
            if (!userLastActivity[userId] || activityDate > userLastActivity[userId]) {
              userLastActivity[userId] = activityDate;
            }
          });
        }
      });

      Object.values(userLastActivity).forEach(lastActivity => {
        if (lastActivity >= oneDayAgo) statistics.activeUsers.daily++;
        if (lastActivity >= oneWeekAgo) statistics.activeUsers.weekly++;
        if (lastActivity >= oneMonthAgo) statistics.activeUsers.monthly++;
      });
    }

    // Log pour le debug
    console.log('Statistiques calculées:', {
      totalUsers: statistics.totalUsers,
      quickEstimates: statistics.quickEstimates,
      detailedEstimates: statistics.detailedEstimates,
      totalCredits: statistics.totalCredits,
      revenue: statistics.revenue,
      activeUsers: statistics.activeUsers
    });

    return statistics;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error('Impossible de récupérer les statistiques');
  }
};